import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import type { GridSize } from '@mui/material';
import { dashboardSectionPaperSx } from '../constants/dashboard-layout';

/**
 * Defers rendering of a loading placeholder until `delayMs` has elapsed.
 *
 * This avoids the visual "flicker" where a skeleton appears and disappears
 * almost instantly on fast API responses. The skeleton is only shown when a
 * request is actually slow enough for the user to notice the wait.
 */
export function useDelayedVisible(delayMs = 250): boolean {
  const [visible, setVisible] = React.useState(delayMs <= 0);

  React.useEffect(() => {
    if (delayMs <= 0) {
      setVisible(true);
      return;
    }
    const id = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(id);
  }, [delayMs]);

  return visible;
}

/** Mirrors the elevated card + icon badge + title used by `PageHeader`. */
export const PageHeaderSkeleton: React.FC = () => (
  <Paper elevation={0} sx={dashboardSectionPaperSx}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: '12px' }} />
        <Box sx={{ minWidth: 0 }}>
          <Skeleton variant="text" width={200} height={36} />
          <Skeleton variant="text" width={130} height={20} />
        </Box>
      </Box>
      <Skeleton variant="rounded" width={150} height={40} sx={{ borderRadius: '12px' }} />
    </Box>
  </Paper>
);

export interface FilterBarSkeletonProps {
  /** Number of filter inputs to mimic. */
  fields?: number;
}

/** Mirrors the search/filter card that sits above most lists. */
export const FilterBarSkeleton: React.FC<FilterBarSkeletonProps> = ({ fields = 3 }) => {
  const span = Math.max(1, Math.floor(12 / Math.min(Math.max(fields, 1), 4))) as GridSize;
  return (
    <Paper elevation={0} sx={{ ...dashboardSectionPaperSx, mb: 3 }}>
      <Grid container spacing={2}>
        {Array.from({ length: fields }).map((_, i) => (
          <Grid item xs={12} sm={span} key={i}>
            <Skeleton variant="rounded" height={40} sx={{ borderRadius: '12px' }} />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export interface CardGridSkeletonProps {
  /** How many placeholder cards to render. */
  count?: number;
  /** Responsive column spans, matching MUI Grid breakpoints. */
  columns?: Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', GridSize>>;
  /** Height of the main body placeholder inside each card. */
  bodyHeight?: number;
}

/** A responsive grid of card-shaped placeholders. */
export const CardGridSkeleton: React.FC<CardGridSkeletonProps> = ({
  count = 8,
  columns = { xs: 12, sm: 6, md: 4, lg: 3 },
  bodyHeight = 120,
}) => (
  <Grid container spacing={2}>
    {Array.from({ length: count }).map((_, i) => (
      <Grid item key={i} {...columns}>
        <Card
          sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 1,
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="text" width="70%" height={24} sx={{ mb: 0.5 }} />
                <Stack direction="row" spacing={0.5}>
                  <Skeleton variant="rounded" width={62} height={20} />
                  <Skeleton variant="rounded" width={48} height={20} />
                </Stack>
              </Box>
              <Skeleton variant="circular" width={28} height={28} />
            </Box>
            <Skeleton variant="rounded" height={bodyHeight} sx={{ borderRadius: '12px' }} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

export interface TableSkeletonProps {
  /** Number of body rows to render. */
  rows?: number;
  /** Number of columns to render. */
  columns?: number;
  /** Wrap the table in an elevated rounded Paper (matches list pages). */
  elevated?: boolean;
}

/** A table-shaped placeholder with a header row and body rows. */
export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 6,
  columns = 5,
  elevated = true,
}) => (
  <TableContainer
    {...(elevated
      ? { component: Paper, elevation: 0, sx: dashboardSectionPaperSx }
      : {})}
  >
    <Table>
      <TableHead>
        <TableRow>
          {Array.from({ length: columns }).map((_, i) => (
            <TableCell key={i} align={i === columns - 1 ? 'right' : 'left'}>
              <Skeleton
                variant="text"
                width={i === columns - 1 ? 60 : '70%'}
                sx={{ display: 'inline-block', ...(i === columns - 1 && { ml: 'auto' }) }}
              />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: rows }).map((_, r) => (
          <TableRow key={r}>
            {Array.from({ length: columns }).map((_, c) => (
              <TableCell key={c} align={c === columns - 1 ? 'right' : 'left'}>
                <Skeleton variant="text" width={c === columns - 1 ? 80 : '85%'} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export interface ListSkeletonProps {
  /** Number of list rows to render. */
  rows?: number;
}

/** A vertical list of row placeholders (title + body + meta line). */
export const ListSkeleton: React.FC<ListSkeletonProps> = ({ rows = 5 }) => (
  <Box>
    {Array.from({ length: rows }).map((_, i) => (
      <Box
        key={i}
        sx={{
          py: 2,
          px: 3,
          borderBottom: i < rows - 1 ? '1px solid' : 'none',
          borderColor: 'divider',
        }}
      >
        <Skeleton variant="text" width="35%" height={24} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width={120} height={16} />
      </Box>
    ))}
  </Box>
);

export interface FormPanelSkeletonProps {
  /** Number of form field rows. */
  fields?: number;
  /** Whether to show tab placeholders above the form. */
  tabs?: boolean;
}

/** Mirrors tabbed form pages (Data Entry, HSE, etc.). */
export const FormPanelSkeleton: React.FC<FormPanelSkeletonProps> = ({
  fields = 6,
  tabs = true,
}) => (
  <Paper elevation={0} sx={{ ...dashboardSectionPaperSx, mb: 0, overflow: 'hidden' }}>
    {tabs && (
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, pb: 0.5 }}>
        <Stack direction="row" spacing={2}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" width={120} height={36} sx={{ borderRadius: '8px' }} />
          ))}
        </Stack>
      </Box>
    )}
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2}>
        {Array.from({ length: fields }).map((_, i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Skeleton variant="text" width={100} height={20} sx={{ mb: 0.75 }} />
            <Skeleton variant="rounded" height={40} sx={{ borderRadius: '12px' }} />
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: '12px' }} />
      </Box>
    </Box>
  </Paper>
);

export type LoadingSkeletonVariant = 'cards' | 'table' | 'list' | 'form';

export interface LoadingSkeletonProps {
  /** Which body layout to mimic. */
  variant?: LoadingSkeletonVariant;
  /** Render the page header placeholder. */
  header?: boolean;
  /** Number of filter inputs, or `false` to omit the filter bar. */
  filters?: number | false;
  /** Rows for `table`/`list` variants. */
  rows?: number;
  /** Columns for the `table` variant. */
  columns?: number;
  /** Card count for the `cards` variant. */
  cardCount?: number;
  /** Form fields for the `form` variant. */
  formFields?: number;
  /** Delay before showing, to avoid flicker on fast responses. */
  delayMs?: number;
}

/**
 * Full-page loading placeholder that mimics a page header, an optional filter
 * bar, and a body (card grid, table, list, or form). Use it in place of a
 * centered `<CircularProgress />` for data-driven pages so the layout doesn't
 * jump when data arrives.
 */
const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  variant = 'cards',
  header = true,
  filters = 3,
  rows = 6,
  columns = 5,
  cardCount = 8,
  formFields = 6,
  delayMs = 250,
}) => {
  const visible = useDelayedVisible(delayMs);
  if (!visible) return null;

  return (
    <Box>
      {header && <PageHeaderSkeleton />}
      {filters !== false && filters > 0 && <FilterBarSkeleton fields={filters} />}
      {variant === 'cards' && <CardGridSkeleton count={cardCount} />}
      {variant === 'table' && <TableSkeleton rows={rows} columns={columns} />}
      {variant === 'list' && <ListSkeleton rows={rows} />}
      {variant === 'form' && <FormPanelSkeleton fields={formFields} />}
    </Box>
  );
};

export default LoadingSkeleton;
